import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ImageOff, Search } from 'lucide-react';
import type { CourseSummary } from '@mlm/shared';
import {
  CoachButton,
  Eyebrow,
  ErrorNote,
  Loading,
  Screen,
  inputClass,
} from '../components/primitives';
import { LearnNav } from '../components/learn-nav';
import { fmtPeso, learn, productImageUrl, store } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { includedInSubscription, subscribedCreators } from '../lib/course-access';
import { qk } from '../lib/queries';

/** What a course tile shows from the product feed. */
type TileProduct = { id: string; name: string; images: string[]; price: string };

const PAGE = 20;

/**
 * The course catalogue. The SERVER decides which courses and in what order
 * (`/storefront/courses`); the priced product feed the Shop already reads is
 * joined on by product id only for the name, picture and price, exactly as the
 * website does, so pricing still runs through the one server-side gate.
 *
 * A VISITOR (not signed in, the tenant showing its courses publicly) gets the
 * same page from the anonymous feed, at the public price, and nothing that
 * needs an account (their slot, their subscriptions).
 */
export function LearnBrowsePage() {
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  const signedIn = !!useAuthStore((s) => s.token);

  const ctx = useQuery({
    queryKey: qk.shopContext,
    queryFn: store.paymentMethods,
    staleTime: 5 * 60_000,
    enabled: signedIn,
  });
  const slotId = ctx.data?.slotId ?? null;
  const products = useQuery({
    queryKey: qk.shop(''),
    queryFn: () => store.catalog({ slotId }),
    enabled: signedIn && !ctx.isPending,
    staleTime: 60_000,
  });
  const publicProducts = useQuery({
    queryKey: qk.publicFeed,
    queryFn: learn.publicProducts,
    enabled: !signedIn,
    staleTime: 60_000,
  });
  const feed = signedIn ? products : publicProducts;
  const courses = useInfiniteQuery({
    queryKey: qk.courseCatalog(search),
    queryFn: ({ pageParam }) =>
      learn.catalog({ search: search || undefined, limit: PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.courses.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    staleTime: 60_000,
  });
  // Only words the tiles; Browse works the same if it fails.
  const subs = useQuery({
    queryKey: qk.mySubscriptions,
    queryFn: learn.subscriptions,
    staleTime: 60_000,
    enabled: signedIn,
  });
  const subscribed = useMemo(() => subscribedCreators(subs.data), [subs.data]);

  const byId = useMemo(() => {
    const rows: TileProduct[] = signedIn
      ? (products.data?.products ?? []).map((p) => ({
          id: p.id,
          name: p.name,
          images: p.images,
          price: p.priceEffective,
        }))
      : (publicProducts.data ?? []).map((p) => ({
          id: p.id,
          name: p.name,
          images: p.images ?? [],
          price: p.price,
        }));
    return new Map(rows.map((p) => [p.id, p]));
  }, [signedIn, products.data, publicProducts.data]);
  const tiles = (courses.data?.pages ?? [])
    .flatMap((p) => p.courses)
    .flatMap((course) => {
      const product = byId.get(course.productId);
      return product ? [{ course, product }] : [];
    });

  return (
    <Screen
      eyebrow="Learn"
      title="Browse courses"
      subtitle="Free courses, paid courses and subscriptions, all in one place."
      wide
    >
      <LearnNav />
      <div className="border-b border-border px-5 py-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(term.trim());
          }}
          className="flex gap-2"
        >
          <label className="sr-only" htmlFor="course-search">
            Search courses
          </label>
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sage-deep"
              aria-hidden
            />
            <input
              id="course-search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search courses"
              className={`${inputClass} pl-9`}
            />
          </div>
          <CoachButton type="submit">Search</CoachButton>
        </form>
      </div>
      {courses.isPending || feed.isPending ? (
        <Loading label="Loading courses" />
      ) : courses.error || feed.error ? (
        <ErrorNote
          error={courses.error ?? feed.error}
          retry={() => {
            void courses.refetch();
            void feed.refetch();
          }}
        />
      ) : tiles.length === 0 ? (
        <div className="px-5 py-14 text-center">
          <Eyebrow>Nothing here</Eyebrow>
          <p className="mt-2 text-[0.93rem] text-ink/70">
            {search ? `No course matches "${search}".` : 'No courses are open yet.'}
          </p>
        </div>
      ) : (
        <>
          <ul className="grid grid-cols-2 gap-px bg-border lg:grid-cols-3">
            {tiles.map(({ course, product }) => (
              <li key={course.courseId} className="bg-background">
                <CourseCard
                  course={course}
                  product={product}
                  included={includedInSubscription(course, subscribed)}
                />
              </li>
            ))}
          </ul>
          {courses.hasNextPage && (
            <div className="px-5 py-6">
              <CoachButton
                block
                onClick={() => void courses.fetchNextPage()}
                disabled={courses.isFetchingNextPage}
              >
                {courses.isFetchingNextPage ? 'Loading' : 'Show more'}
              </CoachButton>
            </div>
          )}
        </>
      )}
    </Screen>
  );
}

/**
 * What the card says instead of a price for a free or subscribers-only course:
 * "Included" when a subscription the member holds already opens it.
 */
function priceLabel(course: CourseSummary, product: TileProduct, included: boolean): string {
  if (course.accessLevel === 'free') return 'Free';
  if (course.accessLevel === 'subscriber') return included ? 'Included' : 'Subscription';
  return fmtPeso(product.price);
}

function CourseCard({
  course,
  product,
  included,
}: {
  course: CourseSummary;
  product: TileProduct;
  included: boolean;
}) {
  return (
    <Link
      to={`/learn/c/${course.productId}`}
      className="flex h-full flex-col p-4 transition-colors hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-verde"
    >
      <div className="relative aspect-video w-full overflow-hidden border border-border bg-surface">
        {product.images.length > 0 ? (
          <img
            src={productImageUrl(product.id, 0)}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sage-deep">
            <ImageOff className="h-6 w-6" aria-hidden />
          </div>
        )}
      </div>
      <p className="mt-3 line-clamp-2 text-[0.93rem] leading-snug text-ink">{product.name}</p>
      {course.creatorName && !course.creatorIsHouse && (
        <p className="mt-1 truncate text-[0.82rem] text-sage-deep">{course.creatorName}</p>
      )}
      <div className="mt-auto pt-2">
        <p className="font-display text-[1.05rem] font-semibold text-ink">
          {priceLabel(course, product, included)}
        </p>
        <Eyebrow className="mt-1">
          {course.lessonCount === 1 ? '1 lesson' : `${course.lessonCount} lessons`}
        </Eyebrow>
      </div>
    </Link>
  );
}
